import { useState } from 'react';
import { Icon } from '@iconify/react';

// Adding to the chart of accounts. The account code is not asked for: the API derives it
// from the parent (01-01-02 -> 01-01-02-01), because several report queries resolve
// accounts by code prefix and a hand-typed code would let the codes and the tree drift.
const AddAccountModal = ({ groups = [], defaultParentId = '', onClose, onSubmit }) => {
    const [form, setForm] = useState({
        parent_account_info_id: defaultParentId || '',
        name: '',
        type: 'account',
        description: '',
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.parent_account_info_id) e.parent_account_info_id = 'Choose the group this sits under';
        if (!form.name.trim()) e.name = 'Account name is required';
        else if (form.name.trim().length < 2) e.name = 'Name must be at least 2 characters';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate() || saving) return;
        setSaving(true);
        try {
            await onSubmit({
                parent_account_info_id: Number(form.parent_account_info_id),
                name: form.name.trim(),
                type: form.type,
                description: form.description.trim() || null,
            });
        } finally {
            setSaving(false);
        }
    };

    const inputCls = (field) =>
        `tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-xs focus:tw-outline-none ${
            errors[field] ? 'tw-border-red-400' : 'tw-border-gray-200 focus:tw-border-blue-400'
        }`;

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <form onSubmit={handleSubmit} className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md">
                <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                    <h3 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:file-tree" className="tw-text-blue-500" />
                        Add to Chart of Accounts
                    </h3>
                    <button type="button" onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:close" />
                    </button>
                </div>

                <div className="tw-p-4 tw-space-y-3">
                    <div>
                        <label className="tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                            Under group <span className="tw-text-red-500">*</span>
                        </label>
                        <select
                            value={form.parent_account_info_id}
                            onChange={(e) => set('parent_account_info_id', e.target.value)}
                            className={inputCls('parent_account_info_id') + ' tw-bg-white'}
                        >
                            <option value="">Select a group</option>
                            {groups.map(g => (
                                <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                        </select>
                        {errors.parent_account_info_id && <p className="tw-text-[10px] tw-text-red-500 tw-mt-1">{errors.parent_account_info_id}</p>}
                    </div>

                    <div>
                        <label className="tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                            Name <span className="tw-text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => set('name', e.target.value)}
                            placeholder="e.g. HDFC Current A/C"
                            maxLength={150}
                            className={inputCls('name')}
                        />
                        {errors.name && <p className="tw-text-[10px] tw-text-red-500 tw-mt-1">{errors.name}</p>}
                    </div>

                    <div>
                        <label className="tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Type</label>
                        <select
                            value={form.type}
                            onChange={(e) => set('type', e.target.value)}
                            className={inputCls('type') + ' tw-bg-white'}
                        >
                            <option value="account">Ledger account — entries can be posted to it</option>
                            <option value="group">Group — holds other accounts, nothing posts to it</option>
                        </select>
                    </div>

                    <div>
                        <label className="tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Description</label>
                        <textarea
                            rows="2"
                            value={form.description}
                            onChange={(e) => set('description', e.target.value)}
                            placeholder="Optional"
                            className={inputCls('description') + ' tw-resize-none'}
                        />
                    </div>

                    <p className="tw-text-[10px] tw-text-gray-400 tw-flex tw-items-center tw-gap-1">
                        <Icon icon="mdi:information-outline" />
                        The account code is assigned automatically from the group you pick.
                    </p>
                </div>

                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100 tw-rounded-b-xl">
                    <button type="button" onClick={onClose} disabled={saving} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-100 disabled:tw-opacity-50">
                        Cancel
                    </button>
                    <button type="submit" disabled={saving} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg disabled:tw-opacity-50">
                        {saving && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                        {saving ? 'Creating...' : 'Create Account'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddAccountModal;
